import { Utente } from '@framework/api-client';
import { parseThemeValidationContext } from '#theme';
import { useMemo } from 'react';
import {
  getSubmissionValidationContext,
  ISubmissionDataProps,
  IValidationContextSubmissionData,
} from '../metadata';
import { getSessionValidationContext } from '../session/getSessionValidationContext';
import { IEngineRawSemanticField } from './IEngineRawSemanticField';
import { parseRawSemanticFields } from './parseSemanticFields';

export interface IEngineValidatorContextUser extends Utente {
  /** Dati personalizzati dell'utente, es. provenienti dal Tema */
  custom: Record<string, any> | undefined;
}

export interface IEngineValidationContextParams {
  /** Utente corrente, necessario per i dati di sessione (solo FO) */
  user: IEngineValidatorContextUser | null | undefined;
  /** Campi semantici */
  semanticFields: IEngineRawSemanticField[] | null | undefined;
  /** Campi semantici della pratica */
  dossierSemanticFields: IEngineRawSemanticField[] | null | undefined;
  /**
   * Metadati della submission (Fase, Stato Integrazione, ...).
   * Struttura raggruppata sotto `metadata` per estensibilità futura.
   */
  metadata?: {
    submission?: ISubmissionDataProps | null;
  } | null;
  /** Altri elementi di validazione (e.g. "tipo validazione", outcomeConstraints, ...) */
  others?: Record<string, any> | null;
}

/**
 * Struttura dati minima per il contesto di validazione.
 */
export interface IEngineValidationContext {
  /** Dati utente */
  session: Record<string, any>;
  /** tag semantici */
  semanticFields: Record<string, any>;
  /** Tag semantici di pratica */
  dossierSemanticFields: Record<string, any>;
  /** Metadati della submission (Fase, Stato Integrazione, ...) */
  metadata: {
    submission: IValidationContextSubmissionData;
  };
  [key: string]: any;
}

/**
 * Funzione che ritorna il context di valdiazione da passare
 * al validatore.
 */
export function getEngineValidationContext(
  params: IEngineValidationContextParams,
): IEngineValidationContext {
  const themeContext = parseThemeValidationContext(params.user);

  return {
    session: {
      ...getSessionValidationContext(params.user),
      ...(themeContext.session ?? {}),
    },
    semanticFields: parseRawSemanticFields(params.semanticFields),
    dossierSemanticFields: parseRawSemanticFields(params.dossierSemanticFields),
    metadata: {
      submission: getSubmissionValidationContext(
        params.metadata?.submission,
      ),
    },
    ...(params.others ?? {}),
  };
}

/**
 * Hook che ritorna il context di valdiazione da passare
 * al formhandler.
 */
export function useEngineValidationContext(
  params: IEngineValidationContextParams,
) {
  // Dati specifici del tema
  const result = useMemo(() => getEngineValidationContext(params), [params]);

  return result;
}
